import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faAngleLeft, faInbox } from '@fortawesome/free-solid-svg-icons';

import { BatchCloningService } from '@main/batch-cloning.service';
import { LoaderService } from '@app-shared/services/loader.service';
import { AlertService } from '@main/alert.service';

import {
  BatchCloneRow,
  BatchCloningTableComponent,
} from '@main/automation/components/batch-cloning-table/batch-cloning-table.component';
import { BatchCloningSummaryComponent } from '@main/automation/components/batch-cloning-summary/batch-cloning-summary.component';
import { SettingService } from '@main/setting.service';
import { Router } from '@angular/router';

type StepType = 'single' | 'mya' | 'expiring';

@Component({
  selector: 'app-automation',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    BatchCloningTableComponent,
    BatchCloningSummaryComponent,
  ],
  templateUrl: './batch-cloning.component.html',
  styleUrls: ['./batch-cloning.component.css'],
})
export class BatchCloningComponent implements OnInit {
  location = inject(Location);

  faAngleLeft = faAngleLeft;
  faInbox = faInbox;

  isLoading = signal(false);
  currentStep = signal(1);

  singleRows = signal<BatchCloneRow[]>([]);
  myaRows = signal<BatchCloneRow[]>([]);
  expiringRows = signal<BatchCloneRow[]>([]);

  stepSelections = signal<{
    single: BatchCloneRow[];
    multiYear: BatchCloneRow[];
    expiring: BatchCloneRow[];
  }>({
    single: [],
    multiYear: [],
    expiring: [],
  });

  notifications = signal({
    email: true,
    sms: false,
    inApp: true,
    superAdmin: false,
    admins: false,
  });

  stepValidity = signal({
    1: false,
    2: false,
    3: false,
  });

  multiYearOptions: any;

  private readonly defaultSelectedYear = new Date().getFullYear() - 1;

  constructor(
    private batchCloningService: BatchCloningService,
    private loaderService: LoaderService,
    private alertService: AlertService,
    private settingsService: SettingService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadMultiOptions();
    this.loadStepRows('single');
  }

  private readonly emptyRows: BatchCloneRow[] = [];
  private readonly emptyIds: string[] = [];

  rowsForCurrentStep = computed<BatchCloneRow[]>(() => {
    switch (this.currentStep()) {
      case 1:
        return this.singleRows();
      case 2:
        return this.myaRows();
      case 3:
        return this.expiringRows();
      default:
        return this.emptyRows;
    }
  });

  loadMultiOptions() {
    this.settingsService.getMultiAgreementTypeOptions().subscribe((options) => {
      this.multiYearOptions = options;
    });
  }

  selectedIdsForCurrentStep = computed<string[]>(() => {
    const selections = this.stepSelections();

    switch (this.currentStep()) {
      case 1:
        return selections.single.map((x) => x.projectId);
      case 2:
        return selections.multiYear.map((x) => x.projectId);
      case 3:
        return selections.expiring.map((x) => x.projectId);
      default:
        return this.emptyIds;
    }
  });

  private getStepType(step: number): StepType | null {
    switch (step) {
      case 1:
        return 'single';
      case 2:
        return 'mya';
      case 3:
        return 'expiring';
      default:
        return null;
    }
  }

  public loadBatchCloningJobsByYear(year: number): void {
    const type = this.getStepType(this.currentStep());
    this.getCloningJobs(type ?? 'single', year);
  }

  private loadStepRows(type: StepType): void {
    const hasRows =
      type === 'single'
        ? this.singleRows().length > 0
        : type === 'mya'
          ? this.myaRows().length > 0
          : this.expiringRows().length > 0;

    if (hasRows) return;

    this.isLoading.set(true);
    this.loaderService.showSpinner();
    this.getCloningJobs(type, this.defaultSelectedYear || null);
  }

  private getCloningJobs(type: StepType, year?: number): void {
    this.batchCloningService.getCloningProjects(type, year).subscribe({
      next: (res) => {
        const rawRows = res?.items ?? res ?? [];
        const rows: BatchCloneRow[] = (rawRows ?? []).map((r: any) => {
          if (type === 'mya') {
            // API returns latestProject* fields for MYA candidates
            const year = r.latestProjectYear ?? r.projectYear ?? r.year ?? null;
            return {
              clientId: r.clientId,
              clientName: r.clientName,
              projectId: r.latestProjectId ?? r.projectId,
              projectName: r.latestProjectName ?? r.projectName,
              year,
              myaId: r.myaId,
              agreementType: r.agreementType ?? r.acceptedProposalAgreementType ?? r.accepted_proposal_agreement_type,
              isCloned: r.isCloned,
              newProjectName: r.newProjectName ?? null,
              newProjectYear: r.newProjectYear ?? (year ? Number(year) + 1 : null),
              isAutoSendProposal: !!r.isAutoSendProposal,
              proposalSendDateAndTime: r.proposalSendDateAndTime ?? null,
              isAutoAccept: !!r.isAutoAccept,
              isAutoInvoice: !!r.isAutoInvoice,
              invoiceAmount: r.invoiceAmount ?? null,
              invoiceType: r.invoiceType ?? 'percentage',
              invoiceIssueDate: r.invoiceIssueDate ?? null,
              invoiceDueDate: r.invoiceDueDate ?? null,
              isAutoSendInvoice: !!r.isAutoSendInvoice,
              invoiceSendDate: r.invoiceSendDate ?? null,
            } as BatchCloneRow;
          }

          if (type === 'expiring') {
            const year = r.projectYear ?? r.year ?? null;
            const normalizedMultiOptions = this.normalizeMultiOptions(r.multiOptions);
            return {
              clientId: r.clientId,
              clientName: r.clientName,
              projectId: r.projectId,
              projectName: r.projectName,
              year,
              myaId: r.myaId,
              agreementType: r.agreementType,
              isCloned: r.isCloned,
              newProjectName: r.newProjectName ?? null,
              newProjectYear: r.newProjectYear ?? (year ? Number(year) + 1 : null),
              isAutoSendProposal: !!r.isAutoSendProposal,
              proposalSendDateAndTime: r.proposalSendDateAndTime ?? null,
              isAutoAccept: !!r.isAutoAccept,
              isAutoInvoice: !!r.isAutoInvoice,
              invoiceAmount: r.invoiceAmount ?? null,
              invoiceType: r.invoiceType ?? 'percentage',
              invoiceIssueDate: r.invoiceIssueDate ?? null,
              invoiceDueDate: r.invoiceDueDate ?? null,
              isAutoSendInvoice: !!r.isAutoSendInvoice,
              invoiceSendDate: r.invoiceSendDate ?? null,
              lastAcceptedProposal: this.getAgreementLabel(r.lastAcceptedProposal) ?? null,
              multiOptions: this.multiYearOptions ?? normalizedMultiOptions,
              selectedMultiOptionKey: null,
              selectedMultiOption: null,
              selectedMultiOptionKeys: [],
              selectedMultiOptions: null,
              autoSelect: null,
            } as BatchCloneRow;
          }

          // single candidates already match BatchCloneRow
          return {
            ...r,
            year: r.year ?? r.projectYear ?? null,
          } as BatchCloneRow;
        });

        if (type === 'single') {
          this.singleRows.set(rows);
        } else if (type === 'mya') {
          this.myaRows.set(rows);
        } else {
          this.expiringRows.set(rows);
        }

        this.isLoading.set(false);
        this.loaderService.hideSpinner();
      },
      error: (error) => {
        this.isLoading.set(false);
        this.loaderService.hideSpinner();
        this.alertService.showErrorAlert(
          error?.error?.message || `Failed to load ${type} cloning candidates.`
        );
      },
    });
  }

  onStepValidation(valid: boolean): void {
    const step = this.currentStep();

    this.stepValidity.update((v) => ({
      ...v,
      [step]: valid,
    }));
  }

  goToStep(step: number): void {
    if (step === this.currentStep()) {
      return;
    }

    if (step < 1 || step > 4) {
      return;
    }

    this.commitCurrentStepRows();
    this.currentStep.set(step);
    const type = this.getStepType(step);

    if (type) {
      this.loadStepRows(type);
    }
  }

  goToNextStep(): void {
    if (this.currentStep() >= 4) {
      return;
    }

    this.commitCurrentStepRows();
    const nextStep = this.currentStep() + 1;
    this.currentStep.set(nextStep);
    const type = this.getStepType(nextStep);

    if (type) {
      this.loadStepRows(type);
    }
  }

  goToPreviousStep(): void {
    if (this.currentStep() <= 1) {
      return;
    }

    this.commitCurrentStepRows();
    const previousStep = this.currentStep() - 1;
    this.currentStep.set(previousStep);
    const type = this.getStepType(previousStep);

    if (type) {
      this.loadStepRows(type);
    }
  }

  private commitCurrentStepRows(): void {
    const step = this.currentStep();
    const selections = this.stepSelections();

    let editedRows: BatchCloneRow[] = [];

    switch (step) {
      case 1:
        editedRows = selections.single;
        break;

      case 2:
        editedRows = selections.multiYear;
        break;

      case 3:
        editedRows = selections.expiring;
        break;

      default:
        return;
    }

    const editedRowsByProjectId = new Map(
      editedRows.map((row) => [row.projectId, row]),
    );

    const mergeRows = (
      currentRows: BatchCloneRow[],
    ): BatchCloneRow[] => {
      return currentRows.map((currentRow) => {
        const editedRow = editedRowsByProjectId.get(
          currentRow.projectId,
        );

        if (!editedRow) {
          return currentRow;
        }

        return {
          ...currentRow,
          ...editedRow,
        };
      });
    };

    switch (step) {
      case 1:
        this.singleRows.update(mergeRows);
        break;

      case 2:
        this.myaRows.update(mergeRows);
        break;

      case 3:
        this.expiringRows.update(mergeRows);
        break;
    }
  }

  onStepClone(selectedRows: BatchCloneRow[]): void {
    const current = this.currentStep();

    this.stepSelections.update((value) => ({
      ...value,
      single: current === 1
        ? selectedRows
        : value.single,
      multiYear: current === 2
        ? selectedRows
        : value.multiYear,
      expiring: current === 3
        ? selectedRows
        : value.expiring,
    }));
  }

  summary = computed(() => {
    const selections = this.stepSelections();
    return {
      singleCount: selections.single.length,
      myaCount: selections.multiYear.length,
      expiringCount: selections.expiring.length,
    };
  });

  updateNotification(
    key: 'email' | 'sms' | 'inApp' | 'superAdmin' | 'admins',
    value: boolean
  ): void {
    this.notifications.update((current) => ({
      ...current,
      [key]: value,
    }));
  }

  private toIsoStartOfDay(value?: Date | string | null): string | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      const dateMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

      if (!dateMatch) {
        return null;
      }

      const [, year, month, day] = dateMatch;

      const date = new Date(
        Date.UTC(
          Number(year),
          Number(month) - 1,
          Number(day),
          0,
          0,
          0,
          0,
        ),
      );

      return Number.isNaN(date.getTime())
        ? null
        : date.toISOString();
    }

    if (Number.isNaN(value.getTime())) {
      return null;
    }

    const date = new Date(
      Date.UTC(
        value.getUTCFullYear(),
        value.getUTCMonth(),
        value.getUTCDate(),
        0,
        0,
        0,
        0,
      ),
    );

    return date.toISOString();
  }

  private toIsoDateTime(value?: Date | string | null): string | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? null
        : value.toISOString();
    }

    const rawValue = value.trim();

    if (!rawValue) {
      return null;
    }

    const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(rawValue);

    let normalizedValue = rawValue;

    if (!hasTimezone) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
        normalizedValue = `${rawValue}T00:00:00Z`;
      } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(rawValue)) {
        normalizedValue = `${rawValue}:00Z`;
      } else {
        normalizedValue = `${rawValue}Z`;
      }
    }

    const date = new Date(normalizedValue);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toISOString();
  }

  private buildNotificationPayload() {
    const n = this.notifications();
    const channels: string[] = [];

    if (n.inApp) channels.push('crm');
    if (n.email) channels.push('email');
    if (n.sms) channels.push('sms');

    return {
      channels,
      recipientUserIds: [],
      sendOnSuccess: true,
      sendOnFailure: true,
      sendOnPartial: true,
    };
  }

  private normalizeMultiOptions(options?: Record<string, any> | null) {
    if (!options) return null;

    return Object.entries(options).reduce((acc, [key, value]) => {
      acc[key] = {
        title: value?.title ?? null,
        acronym: value?.acronym ?? null,
        isEnabled: !!value?.isEnabled,
        numOfYears: value?.numOfYears ?? value?.numOfyears ?? null,
      };
      return acc;
    }, {} as Record<string, { title: string | null; acronym: string | null; isEnabled: boolean; numOfYears: number | null }>);
  }

  private getSelectedMultiOptionKeys(row: BatchCloneRow): string[] {
    const selectedKeys = Array.isArray(row.selectedMultiOptionKeys)
      ? row.selectedMultiOptionKeys
      : [];
    const legacyKey = row.selectedMultiOptionKey ? [row.selectedMultiOptionKey] : [];

    return Array.from(new Set([...selectedKeys, ...legacyKey].filter(Boolean)));
  }

  private buildSelectedMultiOptionsPayload(row: BatchCloneRow): Record<string, any> | null {
    if (row.selectedMultiOptions && Object.keys(row.selectedMultiOptions).length > 0) {
      return row.selectedMultiOptions;
    }

    const selectedKeys = this.getSelectedMultiOptionKeys(row);
    if (selectedKeys.length === 0) return null;

    const sourceOptions = row.multiOptions ?? this.multiYearOptions ?? {};
    const selected = selectedKeys.reduce((acc, key) => {
      const option = sourceOptions?.[key];
      if (!option) return acc;

      acc[key] = {
        title: option.title ?? null,
        acronym: option.acronym ?? null,
        isEnabled: !!option.isEnabled,
        numOfYears: option.numOfYears ?? option.numOfyears ?? null,
      };

      return acc;
    }, {} as Record<string, any>);

    return Object.keys(selected).length > 0 ? selected : null;
  }

  private buildAutomationProjectPayload(row: BatchCloneRow) {
    return {
      newProjectName: row.newProjectName?.trim() || null,

      isAutoSendProposal: !!row.isAutoSendProposal,
      proposalSendDateAndTime: row.isAutoSendProposal
        ? this.toIsoDateTime(row.proposalSendDateAndTime)
        : null,

      isAutoAccept: !!row.isAutoAccept,

      isAutoInvoice: !!row.isAutoInvoice,
      invoiceAmount: row.invoiceAmount ?? null,
      invoiceType: row.invoiceType || 'percentage',

      invoiceIssueDate: row.isAutoInvoice
        ? this.toIsoStartOfDay(row.invoiceIssueDate)
        : null,

      invoiceDueDate: row.isAutoInvoice
        ? this.toIsoStartOfDay(row.invoiceDueDate)
        : null,

      isAutoSendInvoice: !!row.isAutoSendInvoice,

      invoiceSendDate: row.isAutoSendInvoice
        ? this.toIsoDateTime(row.invoiceSendDate)
        : null
    };
  }

  submitJob(): void {
    const selections = this.stepSelections();
    const user = JSON.parse(sessionStorage.getItem('CurrentUser'));

    
    const payload = {
      singleProjects: selections.single.map((row) => ({
        clientId: row.clientId,
        projectId: row.projectId,
        ...this.buildAutomationProjectPayload(row),
      })),

      myaProjects: selections.multiYear.map((row) => ({
        clientId: row.clientId,
        projectId: row.projectId,
        myaId: row.myaId,
        newProjectYear: row.newProjectYear ?? ((row.year ?? 0) ? Number(row.year) + 1 : null),
        ...this.buildAutomationProjectPayload(row),
      })),

      expiringProjects: selections.expiring.map((row) => {
        const selectedMultiOptionKeys = this.getSelectedMultiOptionKeys(row);
        const autoSelect = row.autoSelect ?? null;

        return {
          clientId: row.clientId,
          projectId: row.projectId,
          myaId: row.myaId,
          newProjectYear: row.newProjectYear ?? ((row.year ?? 0) ? Number(row.year) + 1 : null),
          ...this.buildAutomationProjectPayload(row),

          // For expiring only: auto accept is based on the single selected MYA option.
          isAutoAccept: !!autoSelect,
          autoSelect,
          selectedMultiOptionKeys,
          multiOptions: this.buildSelectedMultiOptionsPayload(row),
        };
      }),

      notifications: this.buildNotificationPayload(),
      organizationId: user.organizationId,
      userId:user.id
      
    };

    // console.log("data");
    // console.log(JSON.stringify(payload));

    this.loaderService.showSpinner();

    this.batchCloningService.postCloningJobs(payload).subscribe({
      next: (res) => {
        this.loaderService.hideSpinner();
        this.alertService.showSuccessAlert('Successfully created batch cloning job!');
        this.router.navigateByUrl('/automation');
      },
      error: (error) => {
        this.loaderService.hideSpinner();
        this.alertService.showErrorAlert(
          error?.error?.message || 'An error occurred while creating the cloning job.'
        );
      },
    });
  }

  getAgreementLabel(value: string | null | undefined): string {
    if (!value) return '';

    const match = value.match(/of\s+(\d+)/i);
    if (!match) return '';
    const year = Number(match[1]);
    return `${year} Year Agreement`;
  }
}